import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { GlobalNav } from "../shared/GlobalNav";
import { useToast } from "../../hooks/use-toast";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import {
  Settings, Upload, Eye, EyeOff, Check, CheckCircle, AlertCircle,
  ExternalLink, Copy, Crown, Lock, Mail, Shield, Zap, X, Trash2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LogoUploadSection = ({ settings, setSettings, user, token, toast }) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Check if user has subscription (assuming paid users have a subscription field)
  const hasSubscription = user?.subscription_status === 'active' || user?.is_premium || user?.plan !== 'free';

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (!hasSubscription) {
      toast({
        title: "Subscription Required",
        description: "Logo upload is available for premium subscribers only.",
        variant: "destructive"
      });
      return;
    }

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (!hasSubscription) {
      toast({
        title: "Subscription Required",
        description: "Logo upload is available for premium subscribers only.",
        variant: "destructive"
      });
      return;
    }

    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid File Type",
        description: "Please upload an image file (PNG, JPG, SVG, etc.)",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "File Too Large", 
        description: "Please upload an image smaller than 2MB",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);

    try {
      // Convert to base64
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });

      // Update settings locally
      setSettings({...settings, logo: base64});

      // Save to backend
      await axios.put(`${API}/settings`, {
        logo_url: base64
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Logo Uploaded",
        description: "Your logo has been uploaded and will appear on your invoices and contracts."
      });
    } catch (error) {
      toast({
        title: "Upload Failed",
        description: "Failed to upload logo. Please try again.",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    if (!hasSubscription) return;

    try {
      setSettings({...settings, logo: ''});
      
      await axios.put(`${API}/settings`, {
        logo_url: null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Logo Removed",
        description: "Your logo has been removed from documents."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove logo.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="font-body">Company Logo</Label>
        {!hasSubscription && (
          <Badge className="bg-yellow-100 text-yellow-800 text-xs">
            Premium Feature
          </Badge>
        )}
      </div>

      {hasSubscription ? (
        <div className="space-y-4">
          {/* Current Logo Preview */}
          {settings.logo && (
            <div className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex-shrink-0">
                <img
                  src={settings.logo}
                  alt="Company Logo"
                  className="h-16 w-16 object-contain rounded border"
                />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 font-body">Current Logo</p>
                <p className="text-xs text-gray-500 font-body">This logo will appear on your invoices and contracts</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={removeLogo}
                className="text-red-600 hover:text-red-700 font-body"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Remove
              </Button>
            </div>
          )}

          {/* Upload Area */}
          <div
            className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragActive 
                ? 'border-[#e6007e] bg-pink-50' 
                : 'border-gray-300 hover:border-gray-400'
            } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleChange}
            />
            
            {uploading ? (
              <div className="space-y-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#e6007e] mx-auto"></div>
                <p className="text-sm text-gray-600 font-body">Uploading logo...</p>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="w-8 h-8 text-gray-400 mx-auto" />
                <div>
                  <p className="text-sm text-gray-600 font-body">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="text-[#e6007e] hover:text-[#c4006b] font-medium"
                    >
                      Click to upload
                    </button>
                    {' '}or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 font-body mt-1">
                    PNG, JPG, SVG up to 2MB
                  </p>
                </div>
              </div>
            )}
          </div>

          {!settings.logo && (
            <div className="text-xs text-gray-500 font-body">
              💡 Your logo will automatically appear on all invoices and contracts generated for your clients.
            </div>
          )}
        </div>
      ) : (
        <div className="p-6 border border-gray-200 rounded-lg bg-gray-50 text-center">
          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
          <h3 className="text-sm font-medium text-gray-900 mb-2 font-heading">Premium Feature</h3>
          <p className="text-sm text-gray-600 font-body mb-4">
            Upload your company logo to appear on invoices and contracts. Available with premium subscription.
          </p>
          <Button 
            className="bg-[#e6007e] hover:bg-[#c4006b] text-white font-body"
            onClick={() => {
              toast({
                title: "Premium Feature",
                description: "Contact support to upgrade your account for logo upload functionality."
              });
            }}
          >
            Upgrade to Premium
          </Button>
        </div>
      )}

      {/* Manual URL Input (fallback) */}
      {hasSubscription && (
        <div className="pt-4 border-t border-gray-200">
          <Label htmlFor="logoUrl" className="text-xs font-body text-gray-500">
            Or enter logo URL manually:
          </Label>
          <Input
            id="logoUrl"
            value={settings.logo.startsWith('data:') ? '' : settings.logo}
            onChange={(e) => setSettings({...settings, logo: e.target.value})}
            placeholder="https://example.com/logo.png"
            className="mt-1 text-xs font-body"
          />
        </div>
      )}
    </div>
  );
};

const SettingsPage = () => {
  const { user, logout } = useAuth();
  const { token } = useAuth();
  
  // Check if user has Paify Plus or Embedded subscription
  const hasPlusSubscription = user?.subscription_plan === 'paidd_plus' || user?.subscription_plan === 'embedded';
  
  const [settings, setSettings] = useState({
    companyName: '',
    logo: '',
    brandColor: '#e6007e',
    supportEmail: '',
    defaultVatRate: 20,
    receiptsEnabled: true,
    remindersEnabled: true,
    trustBadgeEnabled: true,
    trustBadgeInfo: 'Verified by Paify - Secure payments and professional service',
    thankYouMessage: '',
    completionMessage: '',
    googleReviewEnabled: false,
    reviewRequestDelay: 7,
    completionConfirmationEnabled: true,
    latePaymentEnabled: true,
    interestRate: 8.0,
    baseRate: 5.25,
    recoveryFee: 40.0,
    // Payment Reminder Settings
    paymentReminder7Days: false,
    paymentReminder3Days: false,
    paymentReminder1Day: false,
    // Enhanced Communication Settings
    sendReceipts: true,
    paymentReminders: true,
    thankYouMessages: true,
    completionConfirmations: true,
    googleReviewRequests: true,
    googleReviewForSubscriptions: true,
    googleReviewLink: '',
    reviewRequestDelayDays: 7,
    paymentConfirmationMessage: 'Thank you for choosing {business_name}! We look forward to working with you.',
    completionMessageTemplate: 'Your project has been completed successfully. We appreciate your business!',
    reviewRequestMessage: "We'd love to hear about your experience! Please leave us a review.",
    reminderMessage: 'This is a friendly reminder that payment for {invoice_number} is due on {due_date}.',
    // Email Configuration - SMTP approach
    emailEnabled: true,
    businessEmail: '',
    businessName: '',
    businessPhone: '',
    emailPassword: '',
    smtpHost: '',
    smtpPort: 587,
    emailSignature: '',
    emailTemplate: 'professional',
    // Business Details for Contract Compliance
    businessLegalName: '',
    businessTradingName: '',
    businessAddressLine1: '',
    businessAddressLine2: '',
    businessCity: '',
    businessPostcode: '',
    businessCountry: 'United Kingdom',
    companyNumber: '',
    vatNumber: '',
    authorizedSignatoryName: '',
    authorizedSignatoryPosition: 'Director',
    // DocuSign Integration Settings
    docusignIntegrationKey: '',
    docusignUserId: '',
    docusignAccountId: '',
    docusignPrivateKey: '',
    docusignBasePath: 'https://demo.docusign.net/restapi',
    docusignOauthBasePath: 'https://account-d.docusign.com',
    docusignEnabled: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch communication settings separately
      const commResponse = await axios.get(`${API}/communications/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      const commData = commResponse.data;
      
      setSettings({
        companyName: data.company_name,
        logo: data.logo_url || '',
        brandColor: data.brand_color,
        supportEmail: data.support_email,
        defaultVatRate: data.default_vat_rate,
        receiptsEnabled: data.receipts_enabled,
        remindersEnabled: data.reminders_enabled,
        trustBadgeEnabled: data.trust_badge_enabled,
        trustBadgeInfo: data.trust_badge_text,
        thankYouMessage: data.thank_you_message || '',
        completionMessage: data.completion_message || '',
        googleReviewEnabled: data.google_review_enabled || false,
        reviewRequestDelay: data.review_request_delay || 7,
        completionConfirmationEnabled: data.completion_confirmation_enabled || true,
        latePaymentEnabled: data.late_payment_protection_enabled !== undefined ? data.late_payment_protection_enabled : true,
        interestRate: data.late_payment_interest_rate || 8.0,
        baseRate: data.bank_of_england_base_rate || 5.25,
        recoveryFee: data.late_payment_recovery_fee || 40.0,
        // Payment Reminder Settings
        paymentReminder7Days: data.payment_reminder_7_days || false,
        paymentReminder3Days: data.payment_reminder_3_days || false,
        paymentReminder1Day: data.payment_reminder_1_day || false,
        // Enhanced Communication Settings
        sendReceipts: commData.send_receipts,
        paymentReminders: commData.payment_reminders,
        dunningStages: Array.isArray(commData.dunning_stages) && commData.dunning_stages.length
          ? commData.dunning_stages
          : [1, 7, 17, 27, 37, 47, 57],
        thankYouMessages: commData.thank_you_messages,
        completionConfirmations: commData.completion_confirmations,
        googleReviewRequests: commData.google_review_requests,
        googleReviewForSubscriptions: commData.google_review_for_subscriptions !== false,
        googleReviewLink: data.google_review_url || commData.google_review_link || '',
        reviewRequestDelayDays: commData.review_request_delay_days,
        paymentConfirmationMessage: commData.payment_confirmation_message,
        completionMessageTemplate: commData.completion_message,
        reviewRequestMessage: commData.review_request_message,
        reminderMessage: commData.reminder_message,
        // Email Configuration - SMTP approach
        emailEnabled: data.email_enabled !== undefined ? data.email_enabled : true,
        businessEmail: data.business_email || '',
        businessName: data.business_name || '',
        businessPhone: data.business_phone || '',
        emailPassword: data.email_password || '',
        smtpHost: data.smtp_host || '',
        smtpPort: data.smtp_port || 587,
        emailSignature: data.email_signature || '',
        emailTemplate: data.email_template || 'professional',
        // Business Details for Contract Compliance
        businessLegalName: data.business_legal_name || '',
        businessTradingName: data.business_trading_name || '',
        businessAddressLine1: data.business_address_line1 || '',
        businessAddressLine2: data.business_address_line2 || '',
        businessCity: data.business_city || '',
        businessPostcode: data.business_postcode || '',
        businessCountry: data.business_country || 'United Kingdom',
        companyNumber: data.company_number || '',
        vatNumber: data.vat_number || '',
        authorizedSignatoryName: data.authorized_signatory_name || '',
        authorizedSignatoryPosition: data.authorized_signatory_position || '',
        // DocuSign Settings
        docusignIntegrationKey: data.docusign_integration_key || '',
        docusignUserId: data.docusign_user_id || '',
        docusignAccountId: data.docusign_account_id || '',
        docusignPrivateKey: data.docusign_private_key || '',
        docusignBasePath: data.docusign_base_path || 'https://demo.docusign.net/restapi',
        docusignOauthBasePath: data.docusign_oauth_base_path || 'https://account-d.docusign.com',
        docusignEnabled: data.docusign_enabled || false
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save general settings
      const payload = {
        logo_url: settings.logo || null,
        brand_color: settings.brandColor,
        support_email: settings.supportEmail,
        default_vat_rate: settings.defaultVatRate,
        receipts_enabled: settings.receiptsEnabled,
        reminders_enabled: settings.remindersEnabled,
        trust_badge_enabled: settings.trustBadgeEnabled,
        trust_badge_text: settings.trustBadgeInfo,
        thank_you_message: settings.thankYouMessage,
        completion_message: settings.completionMessage,
        google_review_enabled: settings.googleReviewEnabled,
        google_review_url: settings.googleReviewLink || null,
        review_request_delay: settings.reviewRequestDelay,
        completion_confirmation_enabled: settings.completionConfirmationEnabled,
        late_payment_protection_enabled: settings.latePaymentEnabled,
        late_payment_interest_rate: settings.interestRate,
        bank_of_england_base_rate: settings.baseRate,
        late_payment_recovery_fee: settings.recoveryFee,
        // Payment Reminder Settings
        payment_reminder_7_days: settings.paymentReminder7Days || false,
        payment_reminder_3_days: settings.paymentReminder3Days || false,
        payment_reminder_1_day: settings.paymentReminder1Day || false,
        // Email Configuration - SMTP approach
        email_enabled: settings.emailEnabled,
        business_email: settings.businessEmail,
        business_name: settings.businessName,
        business_phone: settings.businessPhone,
        email_password: settings.emailPassword,
        smtp_host: settings.smtpHost,
        smtp_port: settings.smtpPort,
        email_signature: settings.emailSignature,
        email_template: settings.emailTemplate,
        // Business Details for Contract Compliance
        business_legal_name: settings.businessLegalName,
        business_trading_name: settings.businessTradingName,
        business_address_line1: settings.businessAddressLine1,
        business_address_line2: settings.businessAddressLine2,
        business_city: settings.businessCity,
        business_postcode: settings.businessPostcode,
        business_country: settings.businessCountry,
        company_number: settings.companyNumber,
        vat_number: settings.vatNumber,
        authorized_signatory_name: settings.authorizedSignatoryName,
        authorized_signatory_position: settings.authorizedSignatoryPosition,
        // DocuSign Integration Settings
        docusign_integration_key: settings.docusignIntegrationKey,
        docusign_user_id: settings.docusignUserId,
        docusign_account_id: settings.docusignAccountId,
        docusign_private_key: settings.docusignPrivateKey,
        docusign_base_path: settings.docusignBasePath,
        docusign_oauth_base_path: settings.docusignOauthBasePath,
        docusign_enabled: settings.docusignEnabled
      };

      await axios.put(`${API}/settings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Save communication settings separately
      const commPayload = {
        send_receipts: settings.sendReceipts,
        payment_reminders: settings.paymentReminders,
        dunning_stages: settings.dunningStages || [1, 7, 17, 27, 37, 47, 57],
        thank_you_messages: settings.thankYouMessages,
        completion_confirmations: settings.completionConfirmations,
        google_review_requests: settings.googleReviewRequests,
        google_review_for_subscriptions: settings.googleReviewForSubscriptions !== false,
        google_review_link: settings.googleReviewLink,
        review_request_delay_days: settings.reviewRequestDelayDays,
        payment_confirmation_message: settings.paymentConfirmationMessage,
        completion_message: settings.completionMessageTemplate,
        review_request_message: settings.reviewRequestMessage,
        payment_reminder_message: settings.reminderMessage,
        reminder_message: settings.reminderMessage
      };

      await axios.post(`${API}/communications/settings`, commPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Settings saved",
        description: "Your settings have been updated successfully."
      });
      
      // Refresh settings to get latest data
      fetchSettings();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  // Fetch all settings

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalNav user={user} onLogout={logout} currentPage="settings" />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e] mx-auto"></div>
            <p className="mt-4 text-gray-600 font-body">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNav user={user} onLogout={logout} currentPage="settings" />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 font-heading">Settings</h1>
          <p className="text-gray-600 font-body">Manage your account and business settings.</p>
        </div>

        <div className="space-y-8">
          {/* Business Details for Contracts */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading">Business Details for Contracts</CardTitle>
              <p className="text-sm text-gray-600 font-body">Required for government-compliant service agreements</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="businessLegalName" className="font-body">Legal Business Name *</Label>
                  <Input
                    id="businessLegalName"
                    value={settings.businessLegalName}
                    onChange={(e) => setSettings({...settings, businessLegalName: e.target.value})}
                    placeholder="Your Company Ltd"
                    className="font-body"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="businessTradingName" className="font-body">Trading Name (if different)</Label>
                  <Input
                    id="businessTradingName"
                    value={settings.businessTradingName}
                    onChange={(e) => setSettings({...settings, businessTradingName: e.target.value})}
                    placeholder="Trading As Name"
                    className="font-body"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="businessAddressLine1" className="font-body">Business Address Line 1 *</Label>
                  <Input
                    id="businessAddressLine1"
                    value={settings.businessAddressLine1}
                    onChange={(e) => setSettings({...settings, businessAddressLine1: e.target.value})}
                    placeholder="123 Business Street"
                    className="font-body"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="businessAddressLine2" className="font-body">Address Line 2</Label>
                  <Input
                    id="businessAddressLine2"
                    value={settings.businessAddressLine2}
                    onChange={(e) => setSettings({...settings, businessAddressLine2: e.target.value})}
                    placeholder="Suite 456"
                    className="font-body"
                  />
                </div>
                <div>
                  <Label htmlFor="businessCity" className="font-body">City *</Label>
                  <Input
                    id="businessCity"
                    value={settings.businessCity}
                    onChange={(e) => setSettings({...settings, businessCity: e.target.value})}
                    placeholder="London"
                    className="font-body"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="businessPostcode" className="font-body">Postcode *</Label>
                  <Input
                    id="businessPostcode"
                    value={settings.businessPostcode}
                    onChange={(e) => setSettings({...settings, businessPostcode: e.target.value})}
                    placeholder="SW1A 1AA"
                    className="font-body"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="companyNumber" className="font-body">Company Number (if incorporated)</Label>
                  <Input
                    id="companyNumber"
                    value={settings.companyNumber}
                    onChange={(e) => setSettings({...settings, companyNumber: e.target.value})}
                    placeholder="12345678"
                    className="font-body"
                  />
                  <p className="text-xs text-gray-500 mt-1 font-body">Companies House registration number</p>
                </div>
                <div>
                  <Label htmlFor="vatNumber" className="font-body">VAT Number (if registered)</Label>
                  <Input
                    id="vatNumber"
                    value={settings.vatNumber}
                    onChange={(e) => setSettings({...settings, vatNumber: e.target.value})}
                    placeholder="GB123456789"
                    className="font-body"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="authorizedSignatoryName" className="font-body">Authorized Signatory Name *</Label>
                  <Input
                    id="authorizedSignatoryName"
                    value={settings.authorizedSignatoryName}
                    onChange={(e) => setSettings({...settings, authorizedSignatoryName: e.target.value})}
                    placeholder="John Smith"
                    className="font-body"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1 font-body">Person authorized to sign contracts</p>
                </div>
                <div>
                  <Label htmlFor="authorizedSignatoryPosition" className="font-body">Position/Title *</Label>
                  <Input
                    id="authorizedSignatoryPosition"
                    value={settings.authorizedSignatoryPosition}
                    onChange={(e) => setSettings({...settings, authorizedSignatoryPosition: e.target.value})}
                    placeholder="Director"
                    className="font-body"
                    required
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 font-body">
                  <strong>Required for Legal Compliance:</strong> This information will appear on all service agreements to meet UK contract law requirements for sufficient certainty and enforceability.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* DocuSign Integration section removed - not in use */}

          {/* Company Information */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading">Company Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="companyName" className="font-body">Company Name</Label>
                <Input
                  id="companyName"
                  value={settings.companyName}
                  className="font-body bg-gray-50"
                  readOnly
                  title="Company name cannot be changed for data protection"
                />
                <p className="text-xs text-gray-500 mt-1 font-body">
                  Company name cannot be changed for data protection and security reasons. <a href="mailto:support@paify.io" className="text-[#e6007e] hover:underline">Contact support</a> if you want to change your company name.
                </p>
              </div>
              
              {/* Logo Upload Section - Paify Plus Feature */}
              <div className={`${!hasPlusSubscription ? 'opacity-60' : ''}`}>
                <div className="flex items-center justify-between mb-2">
                  <Label className="font-body">Company Logo</Label>
                  <span className="inline-block px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded">Paify Plus</span>
                </div>
                
                {!hasPlusSubscription ? (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-sm text-purple-800 font-body">
                      Logo upload is available with Paify Plus or Embedded subscription. <a href="/subscriptions" className="text-[#e6007e] hover:underline font-medium">Upgrade now</a>
                    </p>
                  </div>
                ) : (
                  <LogoUploadSection 
                    settings={settings} 
                    setSettings={setSettings}
                    user={user}
                    token={token}
                    toast={toast}
                  />
                )}
              </div>
              
              {/* Brand Color - Paify Plus Feature */}
              <div className={`${!hasPlusSubscription ? 'opacity-60' : ''}`}>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="brandColor" className="font-body">Brand Color</Label>
                  <span className="inline-block px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded">Paify Plus</span>
                </div>
                
                {!hasPlusSubscription ? (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-sm text-purple-800 font-body">
                      Brand color customization is available with Paify Plus or Embedded subscription. <a href="/subscriptions" className="text-[#e6007e] hover:underline font-medium">Upgrade now</a>
                    </p>
                  </div>
                ) : (
                  <div className="flex space-x-2">
                    <Input
                      id="brandColor"
                      type="color"
                      value={settings.brandColor}
                      onChange={(e) => setSettings({...settings, brandColor: e.target.value})}
                      className="w-20"
                    />
                    <Input
                      value={settings.brandColor}
                      onChange={(e) => setSettings({...settings, brandColor: e.target.value})}
                      className="font-mono"
                    />
                  </div>
                )}
              </div>
              
              <div>
                <Label htmlFor="supportEmail" className="font-body">Support Email</Label>
                <Input
                  id="supportEmail"
                  type="email"
                  value={settings.supportEmail}
                  onChange={(e) => setSettings({...settings, supportEmail: e.target.value})}
                  className="font-body"
                />
              </div>

              {/* Trust Badge Section - Paify Plus Feature */}
              <div className={`border-t pt-6 ${!hasPlusSubscription ? 'opacity-60' : ''}`}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold font-heading">Certified Paify Badge</h3>
                    <p className="text-sm text-gray-600 font-body">Display trust badge on client-facing pages</p>
                  </div>
                  <span className="inline-block px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded">Paify Plus</span>
                </div>
                
                {!hasPlusSubscription ? (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-sm text-purple-800 font-body">
                      This feature is available with Paify Plus or Embedded subscription. <a href="/subscriptions" className="text-[#e6007e] hover:underline font-medium">Upgrade now</a>
                    </p>
                  </div>
                ) : (
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="trustBadgeEnabled"
                      checked={settings.trustBadgeEnabled}
                      onCheckedChange={(checked) => setSettings({...settings, trustBadgeEnabled: checked})}
                    />
                    <Label htmlFor="trustBadgeEnabled" className="font-body">Enable trust badge on quotes and checkout pages</Label>
                  </div>
                  
                  {settings.trustBadgeEnabled && (
                    <div>
                      <Label htmlFor="trustBadgeText" className="font-body">Badge Text</Label>
                      <Input
                        id="trustBadgeText"
                        value={settings.trustBadgeInfo}
                        onChange={(e) => setSettings({...settings, trustBadgeInfo: e.target.value})}
                        placeholder="Verified by Paify - Secure payments and paid service"
                        className="font-body"
                      />
                      <p className="text-xs text-gray-500 mt-1 font-body">This text will appear with the Paify verification badge</p>
                    </div>
                  )}
                  
                  {settings.trustBadgeEnabled && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-gray-900 mb-2 font-body">Preview:</p>
                      <div className="flex items-center space-x-2 text-xs text-gray-600">
                        <Shield className="w-4 h-4 text-[#e6007e]" />
                        <span className="font-body">{settings.trustBadgeInfo || 'Verified by Paify - Secure payments and paid service'}</span>
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>

            </CardContent>
          </Card>

          {/* Payment Settings */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading">Payment & Contract Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="defaultVatRate" className="font-body">Default VAT Rate (%) - GBP</Label>
                <Input
                  id="defaultVatRate"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.defaultVatRate}
                  onChange={(e) => setSettings({...settings, defaultVatRate: parseFloat(e.target.value)})}
                  className="font-body"
                />
              </div>

              {/* Late Payment Protection - Paify Plus Feature */}
              <div className={`pt-4 border-t border-gray-200 ${!hasPlusSubscription ? 'opacity-60' : ''}`}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="flex items-center space-x-2">
                      <Label className="font-body text-base">Late Payment Protection</Label>
                      <span className="inline-block px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded">Paify Plus</span>
                    </div>
                    <p className="text-sm text-gray-600 font-body">Apply statutory interest and recovery fees to overdue payments</p>
                  </div>
                  <button
                    onClick={() => hasPlusSubscription && setSettings({...settings, latePaymentEnabled: !settings.latePaymentEnabled})}
                    disabled={!hasPlusSubscription}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.latePaymentEnabled && hasPlusSubscription ? 'bg-[#e6007e]' : 'bg-gray-200'
                    } ${!hasPlusSubscription ? 'cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.latePaymentEnabled && hasPlusSubscription ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {!hasPlusSubscription ? (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-sm text-purple-800 font-body">
                      This feature is available with Paify Plus or Embedded subscription. <a href="/subscriptions" className="text-[#e6007e] hover:underline font-medium">Upgrade now</a>
                    </p>
                  </div>
                ) : settings.latePaymentEnabled && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="interestRate" className="font-body text-sm">Statutory Interest Rate (%)</Label>
                        <Input
                          id="interestRate"
                          type="number"
                          min="0"
                          max="50"
                          step="0.1"
                          value={settings.interestRate}
                          onChange={(e) => setSettings({...settings, interestRate: parseFloat(e.target.value)})}
                          className="font-body"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="baseRate" className="font-body text-sm">Bank of England Base Rate (%)</Label>
                        <Input
                          id="baseRate"
                          type="number"
                          min="0"
                          max="20"
                          step="0.25"
                          value={settings.baseRate}
                          onChange={(e) => setSettings({...settings, baseRate: parseFloat(e.target.value)})}
                          className="font-body"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="recoveryFee" className="font-body text-sm">Recovery Fee (£)</Label>
                        <Input
                          id="recoveryFee"
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={settings.recoveryFee}
                          onChange={(e) => setSettings({...settings, recoveryFee: parseFloat(e.target.value)})}
                          className="font-body"
                        />
                      </div>
                    </div>
                    
                    <div className="bg-white rounded p-3 text-sm font-body">
                      <p><strong>Total Interest Rate:</strong> {(settings.interestRate + settings.baseRate).toFixed(2)}% per annum</p>
                      <p><strong>Activates on:</strong> Day 61 of non-payment (automatically triggered by Paify)</p>
                      <p><strong>Applied to:</strong> Contract clause, interest calculation, overdue emails</p>
                      <p><strong>Evidence logging:</strong> Automatic for legal compliance</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Enhanced Communication Settings */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading">Communications</CardTitle>
              <p className="text-sm text-gray-600 font-body">Automated client communications and messaging</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Send Receipts */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-body">Send Receipts</Label>
                  <p className="text-sm text-gray-600 font-body">Automatically send payment receipts to clients</p>
                </div>
                <button
                  onClick={() => setSettings({...settings, sendReceipts: !settings.sendReceipts})}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.sendReceipts ? 'bg-[#e6007e]' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.sendReceipts ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* VAT Receipts - Paify Plus Feature */}
              <div className={`border-t pt-6 ${!hasPlusSubscription ? 'opacity-60' : ''}`}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="flex items-center space-x-2">
                      <Label className="font-body">VAT Receipts</Label>
                      <span className="inline-block px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded">Paify Plus</span>
                    </div>
                    <p className="text-sm text-gray-600 font-body">Generate VAT-compliant receipts with detailed breakdowns</p>
                  </div>
                  <button
                    onClick={() => hasPlusSubscription && setSettings({...settings, receiptsEnabled: !settings.receiptsEnabled})}
                    disabled={!hasPlusSubscription}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.receiptsEnabled && hasPlusSubscription ? 'bg-[#e6007e]' : 'bg-gray-200'
                    } ${!hasPlusSubscription ? 'cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.receiptsEnabled && hasPlusSubscription ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                
                {!hasPlusSubscription ? (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-sm text-purple-800 font-body">
                      This feature is available with Paify Plus or Embedded subscription. <a href="/subscriptions" className="text-[#e6007e] hover:underline font-medium">Upgrade now</a>
                    </p>
                  </div>
                ) : settings.receiptsEnabled && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                    <h4 className="font-medium text-blue-900 font-body">VAT Receipt Features</h4>
                    <ul className="text-sm text-blue-800 space-y-1 font-body">
                      <li>• HMRC-compliant VAT receipts</li>
                      <li>• Automatic VAT number inclusion</li>
                      <li>• Detailed VAT breakdown by rate</li>
                      <li>• Digital signature & verification</li>
                      <li>• Export to PDF for client records</li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Payment Reminders & Dunning - Enhanced for Paify Plus */}
              <div className={`border-t pt-6 ${!hasPlusSubscription ? 'opacity-60' : ''}`}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="flex items-center space-x-2">
                      <Label className="font-body">Reminders & Dunning</Label>
                      <span className="inline-block px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded">Paify Plus</span>
                    </div>
                    <p className="text-sm text-gray-600 font-body">Automated payment reminders with escalating sequences</p>
                  </div>
                  <button
                    onClick={() => hasPlusSubscription && setSettings({...settings, paymentReminders: !settings.paymentReminders})}
                    disabled={!hasPlusSubscription}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.paymentReminders && hasPlusSubscription ? 'bg-[#e6007e]' : 'bg-gray-200'
                    } ${!hasPlusSubscription ? 'cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.paymentReminders && hasPlusSubscription ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                
                {!hasPlusSubscription ? (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-sm text-purple-800 font-body">
                      This feature is available with Paify Plus or Embedded subscription. <a href="/subscriptions" className="text-[#e6007e] hover:underline font-medium">Upgrade now</a>
                    </p>
                  </div>
                ) : settings.paymentReminders && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-4">
                    <h4 className="font-medium text-yellow-900 font-body">Automated Dunning Schedule</h4>
                    <p className="text-sm text-yellow-800 font-body">Click a stage to enable or disable that reminder. Disabled stages will be skipped.</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { day: 1, label: "Day 1", desc: "Friendly reminder" },
                        { day: 7, label: "Day 7", desc: "Second reminder" },
                        { day: 17, label: "Day 17", desc: "Follow-up" },
                        { day: 27, label: "Day 27", desc: "Follow-up" },
                        { day: 37, label: "Day 37", desc: "Follow-up" },
                        { day: 47, label: "Day 47", desc: "Follow-up + warning" },
                        { day: 57, label: "Day 57", desc: "Final warning" },
                      ].map((item) => {
                        const stages = settings.dunningStages || [];
                        const isActive = stages.includes(item.day);
                        return (
                          <button
                            type="button"
                            key={item.day}
                            data-testid={`dunning-stage-${item.day}`}
                            onClick={() => {
                              const next = isActive
                                ? stages.filter((d) => d !== item.day)
                                : [...stages, item.day].sort((a, b) => a - b);
                              setSettings({ ...settings, dunningStages: next });
                            }}
                            className={`text-left rounded p-2 transition ${
                              isActive
                                ? "bg-white border border-yellow-300 shadow-sm"
                                : "bg-gray-100 border border-gray-200 opacity-50 hover:opacity-75"
                            }`}
                          >
                            <span className={`text-xs font-semibold font-body block ${isActive ? "text-yellow-900" : "text-gray-600"}`}>
                              {item.label}{!isActive && " (off)"}
                            </span>
                            <span className={`text-xs font-body ${isActive ? "text-yellow-700" : "text-gray-500"}`}>
                              {item.desc}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded p-3">
                      <p className="text-sm text-red-800 font-body">
                        <strong>Day 61:</strong> Late Payment Protection activates automatically (always on when Protection is enabled). Statutory interest ({(settings.interestRate + settings.baseRate).toFixed(2)}% per annum) and the £{settings.recoveryFee} recovery fee will be applied under the Late Payment of Commercial Debts (Interest) Act 1998.
                      </p>
                    </div>
                    <ul className="text-sm text-yellow-800 space-y-1 font-body">
                      <li>• Reminders use your brand colour and logo (Paify Plus)</li>
                      <li>• Stops automatically once the milestone is paid</li>
                      <li>• Every send is logged for evidence and audit</li>
                    </ul>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Automation Settings */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Zap className="w-5 h-5 text-purple-600" />
                <span className="font-heading">Automation Messages</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Thank-you Messages */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label className="font-body">Thank-you Messages</Label>
                    <p className="text-sm text-gray-600 font-body">Customize messages sent after successful payments</p>
                  </div>
                  <button
                    onClick={() => setSettings({...settings, thankYouMessages: !settings.thankYouMessages})}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.thankYouMessages ? 'bg-[#e6007e]' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.thankYouMessages ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                
                {settings.thankYouMessages && (
                  <div>
                    <Label htmlFor="paymentConfirmationMessage" className="font-body text-sm">Payment Confirmation Message</Label>
                    <Textarea
                      id="paymentConfirmationMessage"
                      value={settings.paymentConfirmationMessage}
                      onChange={(e) => setSettings({...settings, paymentConfirmationMessage: e.target.value})}
                      placeholder="Thank you for choosing {business_name}! We look forward to working with you."
                      className="font-body"
                      rows={3}
                    />
                    <p className="text-xs text-gray-500 font-body mt-1">
                      Available variables: {'{business_name}'}, {'{project_title}'}, {'{client_name}'}
                    </p>
                  </div>
                )}
              </div>

              {/* Project Completion Messages */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label className="font-body">Project Completion Confirmation</Label>
                    <p className="text-sm text-gray-600 font-body">Send automatic completion confirmations to clients</p>
                  </div>
                  <button
                    onClick={() => setSettings({...settings, completionConfirmations: !settings.completionConfirmations})}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.completionConfirmations ? 'bg-[#e6007e]' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.completionConfirmations ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                
                {settings.completionConfirmations && (
                  <div>
                    <Label htmlFor="completionMessageTemplate" className="font-body text-sm">Project Completion Message</Label>
                    <Textarea
                      id="completionMessageTemplate"
                      value={settings.completionMessageTemplate}
                      onChange={(e) => setSettings({...settings, completionMessageTemplate: e.target.value})}
                      placeholder="Your project has been completed successfully. We appreciate your business!"
                      className="font-body"
                      rows={3}
                    />
                  </div>
                )}
              </div>

              {/* Google Review Requests */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label className="font-body">Google Review Requests</Label>
                    <p className="text-sm text-gray-600 font-body">Automatically request reviews after project completion</p>
                  </div>
                  <button
                    onClick={() => setSettings({...settings, googleReviewRequests: !settings.googleReviewRequests})}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.googleReviewRequests ? 'bg-[#e6007e]' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.googleReviewRequests ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                
                {settings.googleReviewRequests && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="googleReviewLink" className="font-body text-sm">Google Review Link *</Label>
                      <Input
                        id="googleReviewLink"
                        value={settings.googleReviewLink}
                        onChange={(e) => setSettings({...settings, googleReviewLink: e.target.value})}
                        placeholder="https://www.google.com/search?q=Your+Business+Name"
                        className="font-body"
                      />
                      <p className="text-xs text-gray-500 font-body mt-1">
                        Find your Google Business profile and copy the review link
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="reviewRequestDelayDays" className="font-body text-sm">Review Request Delay (days)</Label>
                        <Input
                          id="reviewRequestDelayDays"
                          type="number"
                          min="0"
                          max="30"
                          value={settings.reviewRequestDelayDays}
                          onChange={(e) => setSettings({...settings, reviewRequestDelayDays: parseInt(e.target.value) || 7})}
                          className="font-body"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="reviewRequestMessage" className="font-body text-sm">Review Request Message</Label>
                      <Textarea
                        id="reviewRequestMessage"
                        value={settings.reviewRequestMessage}
                        onChange={(e) => setSettings({...settings, reviewRequestMessage: e.target.value})}
                        placeholder="We'd love to hear about your experience! Please leave us a review."
                        className="font-body"
                        rows={3}
                      />
                    </div>

                    {/* Toggle: also send review request after subscription cancellation */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <div>
                        <Label className="font-body text-sm">Also send for cancelled Subscriptions</Label>
                        <p className="text-xs text-gray-600 font-body">Send the Google review request {settings.reviewRequestDelayDays || 7} days after a client subscription is cancelled.</p>
                      </div>
                      <button
                        type="button"
                        data-testid="google-review-for-subscriptions-toggle"
                        onClick={() => setSettings({...settings, googleReviewForSubscriptions: !settings.googleReviewForSubscriptions})}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          settings.googleReviewForSubscriptions ? 'bg-[#e6007e]' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            settings.googleReviewForSubscriptions ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Payment Reminder Messages */}
              {settings.paymentReminders && (
                <div className="pt-4 border-t border-gray-200">
                  <Label className="font-body text-sm">Payment Reminder Message</Label>
                  <Textarea
                    value={settings.reminderMessage}
                    onChange={(e) => setSettings({...settings, reminderMessage: e.target.value})}
                    placeholder="This is a friendly reminder that payment for {invoice_number} is due on {due_date}."
                    className="font-body mt-2"
                    rows={3}
                  />
                  <p className="text-xs text-gray-500 font-body mt-1">
                    Available variables: {'{invoice_number}'}, {'{due_date}'}, {'{client_name}'}, {'{business_name}'}, {'{days_overdue}'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Trust Badge Settings */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading">Trust Badge</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-body">Enable Trust Badge</Label>
                  <p className="text-sm text-gray-600 font-body">Display verification badge on payment pages</p>
                </div>
                <button
                  onClick={() => setSettings({...settings, trustBadgeEnabled: !settings.trustBadgeEnabled})}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.trustBadgeEnabled ? 'bg-[#e6007e]' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.trustBadgeEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {settings.trustBadgeEnabled && (
                <div>
                  <Label htmlFor="trustBadgeInfo" className="font-body">Trust Badge Text</Label>
                  <Textarea
                    id="trustBadgeInfo"
                    value={settings.trustBadgeInfo}
                    onChange={(e) => setSettings({...settings, trustBadgeInfo: e.target.value})}
                    className="font-body"
                    rows={3}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Email Configuration */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading">Email Configuration</CardTitle>
              <p className="text-sm text-gray-600 font-body">Send payment links and invoices directly from your business email account</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-body">Enable Email Sending</Label>
                  <p className="text-sm text-gray-600 font-body">Send payment links and invoices through your email account</p>
                </div>
                <button
                  onClick={() => setSettings({...settings, emailEnabled: !settings.emailEnabled})}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.emailEnabled ? 'bg-[#e6007e]' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.emailEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              {settings.emailEnabled && (
                <div className="space-y-4 pt-4 border-t">
                  {/* SMTP Email Setup */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <div className="bg-blue-600 rounded-full p-2">
                        <Mail className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-blue-900 font-body">Connect Your Email Account</h4>
                        <p className="text-sm text-blue-700 font-body">Sends emails directly from your business email account</p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Setup Presets */}
                  <div>
                    <Label className="font-body">Quick Setup</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSettings({
                          ...settings,
                          smtpHost: 'smtp.gmail.com',
                          smtpPort: 587
                        })}
                        className="font-body"
                      >
                        Gmail
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSettings({
                          ...settings,
                          smtpHost: 'smtp-mail.outlook.com',
                          smtpPort: 587
                        })}
                        className="font-body"
                      >
                        Outlook
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSettings({
                          ...settings,
                          smtpHost: '',
                          smtpPort: 587
                        })}
                        className="font-body"
                      >
                        Custom
                      </Button>
                    </div>
                  </div>

                  {/* Email Account Configuration */}
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Label htmlFor="businessEmail" className="font-body">Business Email Address *</Label>
                      <Input
                        id="businessEmail"
                        type="email"
                        value={settings.businessEmail}
                        onChange={(e) => setSettings({...settings, businessEmail: e.target.value})}
                        placeholder="hello@yourbusiness.com"
                        className="font-body"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="emailPassword" className="font-body">Email Password / App Password *</Label>
                      <div className="relative">
                        <Input
                          id="emailPassword"
                          type={showEmailPassword ? "text" : "password"}
                          value={settings.emailPassword}
                          onChange={(e) => setSettings({...settings, emailPassword: e.target.value})}
                          placeholder="••••••••••••••••"
                          className="font-body pr-10"
                          required
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowEmailPassword(!showEmailPassword)}
                          className="absolute inset-y-0 right-0 px-3 flex items-center"
                        >
                          {showEmailPassword ? 
                            <EyeOff className="h-4 w-4 text-gray-400" /> : 
                            <Eye className="h-4 w-4 text-gray-400" />
                          }
                        </Button>
                      </div>
                      <p className="text-xs text-orange-600 mt-1 font-body">
                        For Gmail/Outlook: Use App Password, not regular password
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="businessName" className="font-body">Business Name *</Label>
                      <Input
                        id="businessName"
                        value={settings.businessName}
                        onChange={(e) => setSettings({...settings, businessName: e.target.value})}
                        placeholder="Your Business Ltd"
                        className="font-body"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="businessPhone" className="font-body">Phone Number (Optional)</Label>
                      <Input
                        id="businessPhone"
                        value={settings.businessPhone}
                        onChange={(e) => setSettings({...settings, businessPhone: e.target.value})}
                        placeholder="+44 123 456 789"
                        className="font-body"
                      />
                    </div>
                  </div>

                  {/* SMTP Settings */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="smtpHost" className="font-body">SMTP Host *</Label>
                      <Input
                        id="smtpHost"
                        value={settings.smtpHost}
                        onChange={(e) => setSettings({...settings, smtpHost: e.target.value})}
                        placeholder="smtp.gmail.com"
                        className="font-body"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="smtpPort" className="font-body">Port *</Label>
                      <Input
                        id="smtpPort"
                        type="number"
                        value={settings.smtpPort}
                        onChange={(e) => setSettings({...settings, smtpPort: parseInt(e.target.value)})}
                        placeholder="587"
                        className="font-body"
                        required
                      />
                    </div>
                  </div>

                  {/* Email Template */}
                  <div>
                    <Label htmlFor="emailTemplate" className="font-body">Email Template Style</Label>
                    <select
                      id="emailTemplate"
                      value={settings.emailTemplate}
                      onChange={(e) => setSettings({...settings, emailTemplate: e.target.value})}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#e6007e] font-body"
                    >
                      <option value="professional">Professional</option>
                      <option value="friendly">Friendly</option>
                      <option value="minimal">Minimal</option>
                    </select>
                  </div>

                  {/* Email Signature */}
                  <div>
                    <Label htmlFor="emailSignature" className="font-body">Email Signature (Optional)</Label>
                    <Textarea
                      id="emailSignature"
                      value={settings.emailSignature}
                      onChange={(e) => setSettings({...settings, emailSignature: e.target.value})}
                      placeholder="Best regards,&#10;John Smith&#10;Your Business Ltd&#10;&#10;Need help? Just reply to this email!"
                      className="font-body"
                      rows={4}
                    />
                    <div className="text-xs text-gray-500 mt-1 font-body">
                      Leave blank to use: {settings.businessName} • {settings.businessPhone} • {settings.businessEmail}
                    </div>
                  </div>

                  {/* App Password Instructions */}
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <div className="bg-orange-600 rounded-full p-1">
                        <AlertCircle className="w-3 h-3 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-orange-900 font-body">Important: App Passwords Required</h4>
                        <ul className="text-sm text-orange-700 font-body mt-1 space-y-1">
                          <li>• <strong>Gmail:</strong> Generate App Password in Google Account settings</li>
                          <li>• <strong>Outlook:</strong> Generate App Password in Microsoft Account settings</li>
                          <li>• <strong>Business Email:</strong> Use regular password or check with IT</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Test Email */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-green-900 font-body">Test Email Connection</p>
                        <p className="text-sm text-green-700 font-body">Send a test email to verify your settings work</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="font-body"
                        onClick={async () => {
                          try {
                            const response = await axios.post(`${API}/test-email`, {}, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            
                            if (response.data.success) {
                              toast({
                                title: "Test Email Sent!",
                                description: `Test email sent successfully to ${response.data.recipient}. Check your inbox.`
                              });
                            }
                          } catch (error) {
                            console.error('Test email error:', error);
                            toast({
                              title: "Test Email Failed",
                              description: error.response?.data?.detail || "Failed to send test email. Please check your email configuration.",
                              variant: "destructive"
                            });
                          }
                        }}
                      >
                        Send Test
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-black hover:bg-gray-800 text-white font-body"
            >
              {saving ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

// Privacy Policy Page Component


export default SettingsPage;
